import axios, { type AxiosResponse } from 'axios';
import FileSaver from 'file-saver';
import { showLoadingToast, showToast, ToastWrapperInstance } from 'vant';

import { blobValidate, looksLikeJsonErrorBlob } from '@/common/functions';
import errorCode from '@/utils/errorCode';

const baseURL = import.meta.env.VITE_BASE_API;
/** 含图片导出服务端常需数分钟；过短会误杀，过长则网关空包也要干等 */
const EXCEL_DOWNLOAD_TIMEOUT_MS = 3 * 60 * 1000;
const EMPTY_EXPORT_TIP = '导出失败，未获取到文件。含图片导出较慢，可稍后重试或改用不含图片版本';
const NETWORK_ERROR_TIP = '下载文件出现错误，请联系管理员！';
/** 错误文案较长，默认 2s 偏短 */
const RESULT_TOAST_DURATION_MS = 3500;

let downloadLoadingInstance: ToastWrapperInstance | null = null;
let downloadInFlight = false;

function parseBlobErrorMessage(text: string): string {
  try {
    const rspObj = JSON.parse(text);
    return rspObj.msg || rspObj.message || errorCode[rspObj.code] || errorCode.default;
  } catch {
    return EMPTY_EXPORT_TIP;
  }
}

type BlobCheckResult = { ok: true } | { ok: false; message: string };

/** 校验导出 Blob；不直接 toast，由调用方关 loading 后再提示 */
async function checkFileBlob(data: unknown): Promise<BlobCheckResult> {
  if (!(data instanceof Blob) || data.size <= 0) {
    return { ok: false, message: EMPTY_EXPORT_TIP };
  }
  if (!blobValidate(data) || (await looksLikeJsonErrorBlob(data))) {
    return { ok: false, message: parseBlobErrorMessage(await data.text()) };
  }
  return { ok: true };
}

/**
 * Vant Toast 默认单例：showToast 会复用 loading 同一个实例。
 * close 后必须把引用置空，否则 finally 再 close 会立刻关掉结果 toast。
 */
function closeLoadingThenToast(message: string) {
  downloadLoadingInstance?.close();
  downloadLoadingInstance = null;
  showToast({ message, duration: RESULT_TOAST_DURATION_MS });
}

function closeLoadingOnly() {
  downloadLoadingInstance?.close();
  downloadLoadingInstance = null;
}

function startDownloadLoading(message: string) {
  downloadLoadingInstance = showLoadingToast({
    message,
    forbidClick: true,
    duration: 0,
  });
}

function isTimeoutError(error: unknown): boolean {
  const err = error as { code?: string; message?: string };
  return err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
}

type SaveBlobOptions = {
  loadingMessage: string;
  successMessage: string;
  mimeType: string;
  filename: string | ((res: AxiosResponse<Blob>) => string);
  request: () => Promise<AxiosResponse<Blob>>;
};

async function saveBlobDownload(options: SaveBlobOptions) {
  if (downloadInFlight) {
    showToast({ message: '正在下载中，请稍候', duration: RESULT_TOAST_DURATION_MS });
    return;
  }
  downloadInFlight = true;
  startDownloadLoading(options.loadingMessage);
  try {
    const res = await options.request();
    const check = await checkFileBlob(res.data);
    if (check.ok === false) {
      closeLoadingThenToast(check.message);
      return;
    }
    const filename = typeof options.filename === 'function' ? options.filename(res) : options.filename;
    FileSaver.saveAs(new Blob([res.data], { type: options.mimeType }), filename);
    closeLoadingThenToast(options.successMessage);
  } catch (error) {
    console.error(error);
    closeLoadingThenToast(isTimeoutError(error) ? EMPTY_EXPORT_TIP : NETWORK_ERROR_TIP);
  } finally {
    closeLoadingOnly();
    downloadInFlight = false;
  }
}

export default {
  async oss(ossId: string | number) {
    await saveBlobDownload({
      loadingMessage: '加载中...',
      successMessage: '下载成功',
      mimeType: 'application/octet-stream',
      filename: res => decodeURIComponent(res.headers['download-filename'] as string),
      request: () =>
        axios({
          method: 'get',
          url: `${baseURL}/resource/oss/download/${ossId}`,
          responseType: 'blob',
        }),
    });
  },
  async zip(url: string, name: string) {
    await saveBlobDownload({
      loadingMessage: '加载中...',
      successMessage: '下载成功',
      mimeType: 'application/zip',
      filename: name,
      request: () =>
        axios({
          method: 'get',
          url: baseURL + url,
          responseType: 'blob',
        }),
    });
  },
  async excel(url: string, params: any, filename: string) {
    await saveBlobDownload({
      loadingMessage: '正在导出，含图片时可能需要几分钟…',
      successMessage: '导出成功，请查看下载文件',
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      filename,
      request: () =>
        axios({
          method: 'get',
          url: baseURL + url,
          params,
          responseType: 'blob',
          timeout: EXCEL_DOWNLOAD_TIMEOUT_MS,
        }),
    });
  },
};
